import { Button, Modal } from 'components/common'
import { ModalRef } from 'components/common/Modal/Modal'
import { ServiceTime } from 'components/common/types'
import React, { useEffect, useRef, useState } from 'react'
import Box from '../common/Box'
import OrderStepsTemplate from '../OrderStepsTemplate'
import { formatServiceTime } from 'utils/dateHelpers'
import 'react-datepicker/dist/react-datepicker.css'
import setHours from 'date-fns/setHours'
import setMinutes from 'date-fns/setMinutes'
import { isToday } from 'date-fns'

type FormData = {
  serviceTime: ServiceTime | null
  deliveryDate: string
}

type Props = {
  formData: FormData
  goPrevious?: (formData: FormData) => void
  showLeftArrow: boolean
  onSubmit: (formData: FormData) => void
  submitButtonText: string
}

const ServiceTimeStep = ({
  formData,
  goPrevious,
  onSubmit = () => { },
  showLeftArrow = true,
  submitButtonText
}: Props) => {
  const datePicker = useRef()
  const DatePicker = datePicker.current
  const [serviceTime, setServiceTime] = useState(formData.serviceTime)
  const [minTime, setMinTime] = useState<Date | undefined>(undefined);
  const [maxTime, setMaxTime] = useState<Date | undefined>(undefined);
  const [bufferInMinutes, setBufferInMinutes] = useState(120);
  const [selectedTime, setSelectedTime] = useState<Date | undefined>(undefined);
  const [timeSelected, setTimeSelected] = useState<boolean>(false);
  const [deliveryDate, setDeliveryDate] = useState(
    formData.deliveryDate ? formData.deliveryDate : new Date()
  )
  const [_, setRenderTriggered] = useState(false)

  const selectTimeRef = useRef<ModalRef>(null)

  const calculateMinTime = (inputDate) => {
    // If inputDate is not provided, default to current date
    const dateToUse = inputDate ? new Date(inputDate) : new Date();
    const now = new Date();
    const utcNow = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
    const bufferInMinutesStr = localStorage.getItem("BufferInMinutes");
    const locationOffsetStr = localStorage.getItem("LocationOffset");
    const bufferInMinutes = bufferInMinutesStr ? parseInt(bufferInMinutesStr, 10) : 120; // Default to 120 minutes if not found
    const locationOffset = locationOffsetStr ? parseFloat(locationOffsetStr) : 0; // Default to 0 if not found
    setBufferInMinutes(bufferInMinutes);
    // Adjust now with location offset and buffer
    const adjustedTime = new Date(utcNow.getTime() + locationOffset * 3600000 + bufferInMinutes * 60000);
    if (isToday(dateToUse)) {
      setSelectedTime(adjustedTime);
      setMinTime(adjustedTime);
      setMaxTime(setHours(setMinutes(new Date(), 59), 23));
    } else {
      // For future dates, let's reset minTime and maxTime to cover the whole day
      setSelectedTime(new Date(deliveryDate));
      setMinTime(setHours(setMinutes(new Date(dateToUse), 0), 0)); // Start of the day for inputDate
      setMaxTime(setHours(setMinutes(new Date(dateToUse), 59), 23)); // End of the day for inputDate
    }
  };


  useEffect(() => {
    calculateMinTime(null);
  }, []);

  const handleNextClick = () => {
    const date = new Date(deliveryDate);
    if (isToday(date)) {
      const locationOffsetStr = localStorage.getItem("LocationOffset");
      const locationOffset = locationOffsetStr ? parseFloat(locationOffsetStr) : 0;
      // Adjust now with location offset and buffer
      const adjustedTime = new Date(date.getTime() - locationOffset * 3600000);
      setDeliveryDate(adjustedTime);
    }
    onSubmit({
      serviceTime,
      deliveryDate // Submit the date in UTC format
    });
  };

  const handlePrevClick = () => {
    if (showLeftArrow) {
      goPrevious({
        serviceTime,
        deliveryDate
      })
    }
  }

  const handleLaterBoxClick = () => {
    setServiceTime(ServiceTime.LATER)
    selectTimeRef.current?.openModal()
  }

  const onConfirm = () => selectTimeRef.current?.closeModal()

  const onCancel = () => {
    selectTimeRef.current?.closeModal()
    setServiceTime(null)
    setDeliveryDate(new Date())
  }

  const disableNextButton = serviceTime === null

  const disableConfirmButton = deliveryDate === '' || !DatePicker

  useEffect(() => {
    // The purpose of doing this is disabling confirm button untill date picker is loaded.
    // There is weird stuffs going on here. In the future there might be better solution.
    const loadLazyComponent = async () => {
      try {
        // Dynamically import the lazy component
        const module = await import('react-datepicker')
        // Access the default export from the module
        datePicker.current = module.default

        setRenderTriggered(true)
      } catch (error) {
        console.error('Error loading lazy component:', error)
      }
    }

    loadLazyComponent()
  }, [])

  return (
    <>
      <OrderStepsTemplate
        title="When do you want this service"
        nextButtonClick={handleNextClick}
        prevArrowClick={handlePrevClick}
        disableNextButton={disableNextButton}
        showLeftArrow={showLeftArrow}
        buttonText={submitButtonText ?? 'Next'}
      >
        <div className="flex flex-1 flex-col space-y-6">
          <Box
            onClick={handleLaterBoxClick}
            isActive={serviceTime === ServiceTime.LATER}
            className="basis-1/2 items-center justify-center space-y-4"
          >
            <h3 className="flex flex-1 items-center justify-center">
              SCHEDULE A TIME
            </h3>

            {deliveryDate && serviceTime === ServiceTime.LATER && (
              <div>{formatServiceTime(new Date(deliveryDate))}</div>
            )}
          </Box>

          <Box
            onClick={() => setServiceTime(ServiceTime.ASAP)}
            isActive={serviceTime === ServiceTime.ASAP}
            className="basis-1/2 items-center justify-center space-y-4"
          >
            <h3 className="flex flex-1 items-center justify-center">ASAP</h3>
          </Box>
        </div>
      </OrderStepsTemplate>

      <Modal
        ref={selectTimeRef}
        className="h-full md:h-[500px] w-[95%] p-4 md:p-8 md:w-min-[600px]"
        shouldCloseOnEsc={false}
        showCloseIcon={false}
        shouldCloseOnOverlayClick={false}
      >
        <div className="flex flex-col h-full">
          <div className="text-3xl mb-12 text-center font-bold">
            When do you want this service?
          </div>

          <div className="text-2xl mb-4">
            We require at least {bufferInMinutes} minutes for scheduled orders.
          </div>

          {DatePicker ? (
            <DatePicker
              open
              className="w-full rounded-md cursor-pointer  bg-gray-200 p-4"
              selected={selectedTime}
              minDate={new Date()}
              minTime={minTime}
              maxTime={maxTime}
              placeholderText="Select date "
              onChange={(date) => {
                calculateMinTime(date);
                setDeliveryDate(date);
                setSelectedTime(date);
                setTimeSelected(true);
              }}
              showTimeSelect
              dateFormat="EEE, MMM dd, yyyy  hh:mm  a"
            />
          ) : (
            <div className="skeleton h-[36px] w-full"></div>
          )}

          <div className="mt-[300px] flex justify-center space-x-8">
            <Button
              text="Confirm"
              onClick={onConfirm}
              disabled={!timeSelected}
              className="mt-auto bg-green"
            />

            <Button
              text="Cancel"
              onClick={onCancel}
              className="mt-auto bg-red"
            />
          </div>
        </div>
      </Modal>
    </>
  )
}

export default ServiceTimeStep
